import { useRouter } from 'next/router';
import { t } from 'i18next';
import useLocales from '@/hooks/useLocales';
import { useModals } from '@mattjennings/react-modal-stack';
import { AlertDialog, ConfirmDialog } from '@/components/dialogs';
import { useSelector } from 'react-redux';
import useStepper from './checkout/useStepper';
import useErrorTracking from '@/hooks/userTracking/useErrorTracking';

const errorCodes = [
  'LINK_INACTIVE',
  'LINK_EXPIRED',
  'LINK_USED',
  'LINK_NOT_AVAILABLE',
  'BOOKING_EXPIRED',
];

export default function useDialogs() {
  const booking = useSelector((state) => state.booking);
  const { translate, getObjectOrMessage } = useLocales();
  const { openModal, closeModal } = useModals();

  const router = useRouter();

  const { jumpTo } = useStepper();

  const { checkoutErrorShown } = useErrorTracking();

  const getMessageObj = (code) => {
    let message = getObjectOrMessage(`messages.${code}`);
    if (message) {
      if (typeof message !== 'object') {
        if (message && typeof message === 'string') {
          const objbase = getObjectOrMessage(`messages.default`);
          message = {
            ...objbase,
            body: message,
          };
        } else {
          message = translate(`messages.default`);
        }
      }
    } else {
      const objbase = getObjectOrMessage(`messages.default`);
      message = {
        ...objbase,
        body: `${objbase.body}: (${code})`,
      };
    }

    return message;
  };

  const alert = (text, title = 'Lo sentimos.', acceptLabel = 'Entendido') => {
    openModal(AlertDialog, {
      title,
      text,
      acceptLabel,
    });
  };

  const alertLocale = (code) => {
    alert(translate(code));
  };

  const alertBackCode = (code) => {
    let message = getMessageObj(code);

    checkoutErrorShown({
      source: 'backend',
      code,
      message: message.body,
    });
    alert(message.body, message.title, message.button);
  };

  const confirmBackCode = (code, token) => {
    let message = getMessageObj(code);
    const path = router.pathname;

    if (
      (errorCodes.includes(code) && path === '/payment') ||
      code === 'INVALID_SESSION_TOKEN'
    ) {
      const localesPath = `checkout.modals.${code}`;
      const { title, text, acceptLabel } = t(localesPath, {
        returnObjects: true,
      });

      checkoutErrorShown({
        source: 'backend',
        code,
        message: text,
      });
      confirm({
        title,
        text,
        acceptLabel,
        token,
        cb: () => {
          jumpTo('HOME');
        },
      });
      return;
    }
    checkoutErrorShown({
      source: 'backend',
      code,
      message: message.body,
    });
    confirm({
      text: message.body,
      title: message.title,
      acceptLabel: message.button,
      token,
    });
  };

  const confirm = ({
    title,
    text,
    acceptLabel,
    token,
    cb = () => {},
    omitBooking = false,
  }) => {
    openModal(ConfirmDialog, {
      title,
      text,
      acceptLabel,
      token,
      booking: booking?.orderInfo?.number,
      onConfirm: async () => {
        closeModal();
        cb();
      },
      onClose: cb,
      omitBooking,
    });
  };

  return {
    openModal,
    closeModal,
    alert,
    alertLocale,
    alertBackCode,
    confirmBackCode,
    confirm,
  };
}
